import { TFunction } from "i18next";

import {
  formatDurationSecondsCompact,
  getTrafficTypeTranslationKey,
  HOURS_IN_DAY,
  IStationTimingDayBucket,
  IStationTimingPair,
  IStationTimingPairStatsDto,
  percentFormula,
  TTimeHHmm,
} from "@ryde/shared";

import {
  STATION_TIMING_DAY_GROUP_ID_PREFIX,
  STATION_TIMING_DAY_NAME_KEYS,
  STATION_TIMING_FIRST_LEG_NUMBER,
  STATION_TIMING_ON_PLAN_TOLERANCE_SECONDS,
} from "./station-timing-preview.constants";
import {
  EMPTY_LEG_OPTION_SET,
  ILegOptionSet,
  IStationTimingDayRow,
  IStationTimingDelta,
  IStationTimingLegOption,
  IStationTimingLegRow,
  IStationTimingLegRowGroups,
  IStationTimingPreviewSelection,
  IStationTimingPreviewStation,
  StationTimingDeltaToneEnum,
  StationTimingOptionKindEnum,
  TStationTimingHoursRange,
  TStationTimingSelectionMap,
} from "./station-timing-preview.models";

const OPTION_KEY_DELIMITER = ":";
/** Splits an hour into its two digits, which is what keeps the label a literal `HH:mm` for the type. */
const HOUR_TENS_DIVISOR = 10;

const buildOptionKey = ({
  legIndex,
  kind,
  slotIndex,
}: {
  legIndex: number;
  kind: StationTimingOptionKindEnum;
  slotIndex?: number;
}): string =>
  [legIndex, kind, slotIndex]
    .filter(part => part !== undefined)
    .join(OPTION_KEY_DELIMITER);

/** Self-contained name of a leg, used wherever a control or a hidden row has to say which leg it is. */
export const buildStationTimingLegName = (
  row: Pick<
    IStationTimingLegRow,
    "legIndex" | "stationFromName" | "stationToName"
  >,
): string =>
  `${row.legIndex + STATION_TIMING_FIRST_LEG_NUMBER}. ${row.stationFromName} - ${row.stationToName}`;

/** The leg index is the first segment of every option key, so one table-wide handler can decode it. */
export const parseLegIndexFromOptionKey = (optionKey: string): number =>
  Number(optionKey.split(OPTION_KEY_DELIMITER)[0]);

const formatHourLabel = (hour: number): TTimeHHmm =>
  `${Math.floor(hour / HOUR_TENS_DIVISOR)}${hour % HOUR_TENS_DIVISOR}:00`;

/**
 * Traffic slots hold the contiguous local hours merged into one regime, so the window they describe
 * runs from the first hour to the END of the last one. Empty when the option covers no hours at all,
 * which is every option that is not a traffic slot.
 */
export const formatTrafficSlotHoursRange = (
  hours: number[],
): TStationTimingHoursRange | "" => {
  if (!hours.length) {
    return "";
  }

  const firstHour = Math.min(...hours);
  const lastHour = Math.max(...hours);
  return `${formatHourLabel(firstHour)}-${formatHourLabel(lastHour + 1)}`;
};

/**
 * Hour windows are sent as inclusive hour-of-day bounds, so the window a user reads runs to the END of
 * `hourTo`. Wraps around midnight the same way `getHourWindowInTimezone` produces it.
 */
export const formatStationTimingHourWindow = ({
  hourFrom,
  hourTo,
}: {
  hourFrom: number;
  hourTo: number;
}): TStationTimingHoursRange =>
  `${formatHourLabel(hourFrom)}-${formatHourLabel((hourTo + 1) % HOURS_IN_DAY)}`;

/** Translation key of a weekday name, or `undefined` for an index the backend should never send. */
export const getStationTimingDayNameKey = (
  dayOfWeek: number | undefined,
): string | undefined =>
  dayOfWeek === undefined ? undefined : STATION_TIMING_DAY_NAME_KEYS[dayOfWeek];

/** Ties a leg's day expander to the row group it shows and hides. */
export const getStationTimingDayGroupId = (legIndex: number): string =>
  `${STATION_TIMING_DAY_GROUP_ID_PREFIX}-${legIndex}`;

/**
 * The published day buckets of one leg, ordered Sunday-first the way the whole product reads a week.
 * A bucket with an out-of-range day index is dropped rather than rendered under a blank name.
 */
export const buildStationTimingDayRows = (
  buckets: IStationTimingDayBucket[],
): IStationTimingDayRow[] =>
  buckets
    .filter(bucket => getStationTimingDayNameKey(bucket.dayOfWeek))
    .map(bucket => ({
      dayOfWeek: bucket.dayOfWeek,
      datapoints: bucket.datapoints,
      avgSeconds: bucket.avgSeconds,
      medianSeconds: bucket.medianSeconds,
    }))
    .sort((left, right) => left.dayOfWeek - right.dayOfWeek);

export const buildStationTimingOptionLabel = ({
  option,
  t,
}: {
  option: IStationTimingLegOption;
  t: TFunction;
}): string => {
  switch (option.kind) {
    case StationTimingOptionKindEnum.Median:
      return t("stationTiming.OPTION_MEDIAN");
    case StationTimingOptionKindEnum.Average:
      return t("stationTiming.OPTION_AVERAGE");
    case StationTimingOptionKindEnum.Planned:
      return t("stationTiming.COLUMN_PLANNED");
    default:
      return option.traffic
        ? t(getTrafficTypeTranslationKey(option.traffic))
        : "";
  }
};

const resolveDeltaTone = (deltaSeconds: number): StationTimingDeltaToneEnum => {
  if (Math.abs(deltaSeconds) <= STATION_TIMING_ON_PLAN_TOLERANCE_SECONDS) {
    return StationTimingDeltaToneEnum.OnPlan;
  }

  if (deltaSeconds > 0) {
    return StationTimingDeltaToneEnum.Slower;
  }

  return StationTimingDeltaToneEnum.Faster;
};

/**
 * What applying `measuredSeconds` would do to the plan, which is the question the table has to answer
 * without arithmetic. `undefined` when there is no measurement to compare, and a `NoPlan` delta — never
 * a division by zero — when the leg carries no planned time yet.
 */
export const computeStationTimingDelta = ({
  plannedSeconds,
  measuredSeconds,
}: {
  plannedSeconds: number | undefined;
  measuredSeconds: number | null | undefined;
}): IStationTimingDelta | undefined => {
  if (measuredSeconds === null || measuredSeconds === undefined) {
    return undefined;
  }

  // A zero plan is as unusable as a missing one: no leg is planned to take no time, and dividing by it
  // would report an infinite regression.
  if (!plannedSeconds) {
    return {
      tone: StationTimingDeltaToneEnum.NoPlan,
      seconds: undefined,
      percent: undefined,
      measuredSeconds,
    };
  }

  const deltaSeconds = measuredSeconds - plannedSeconds;

  return {
    tone: resolveDeltaTone(deltaSeconds),
    seconds: deltaSeconds,
    percent: percentFormula(deltaSeconds, plannedSeconds),
    measuredSeconds,
  };
};

/** The delta of the measurement the user currently has selected on a leg, the only one they can apply. */
export const computeStationTimingSelectedDelta = ({
  row,
  selectedOptionKey,
}: {
  row: IStationTimingLegRow;
  selectedOptionKey: string | undefined;
}): IStationTimingDelta | undefined => {
  const selectedOption = row.options.find(
    option => option.key === selectedOptionKey,
  );

  return computeStationTimingDelta({
    plannedSeconds: row.plannedSeconds,
    measuredSeconds: selectedOption?.seconds,
  });
};

/**
 * Signed duration of a delta. The sign is what carries "slower" or "faster" for anyone who cannot see
 * the colour, so it is written even though the arrow glyph repeats it.
 */
export const formatStationTimingDeltaSeconds = (
  deltaSeconds: number,
): string => {
  const magnitude = formatDurationSecondsCompact(Math.abs(deltaSeconds));

  if (deltaSeconds > 0) {
    return `+${magnitude}`;
  }

  if (deltaSeconds < 0) {
    return `-${magnitude}`;
  }

  return magnitude;
};

/** Signed share of the planned time, written the same way as the signed duration above it. */
export const formatStationTimingDeltaPercent = (percent: number): string =>
  percent > 0 ? `+${percent}%` : `${percent}%`;

const buildLegOptionSet = ({
  legIndex,
  pair,
  plannedSeconds,
}: {
  legIndex: number;
  pair: IStationTimingPair;
  plannedSeconds: number | undefined;
}): ILegOptionSet => {
  // Always offered, even with no planned time to show: it is the only way to back a single leg out of
  // the radio group again, since a native radio cannot be unchecked.
  const plannedOption: IStationTimingLegOption = {
    key: buildOptionKey({
      legIndex,
      kind: StationTimingOptionKindEnum.Planned,
    }),
    kind: StationTimingOptionKindEnum.Planned,
    seconds: plannedSeconds,
    datapoints: pair.datapoints,
    hours: [],
  };

  const medianOption: IStationTimingLegOption | undefined =
    pair.medianSeconds === null
      ? undefined
      : {
          key: buildOptionKey({
            legIndex,
            kind: StationTimingOptionKindEnum.Median,
          }),
          kind: StationTimingOptionKindEnum.Median,
          seconds: pair.medianSeconds,
          datapoints: pair.datapoints,
          hours: [],
        };

  const averageOption: IStationTimingLegOption | undefined =
    pair.avgSeconds === null
      ? undefined
      : {
          key: buildOptionKey({
            legIndex,
            kind: StationTimingOptionKindEnum.Average,
          }),
          kind: StationTimingOptionKindEnum.Average,
          seconds: pair.avgSeconds,
          datapoints: pair.datapoints,
          hours: [],
        };

  const trafficOptions: IStationTimingLegOption[] = pair.trafficSlots.map(
    (slot, slotIndex) => ({
      key: buildOptionKey({
        legIndex,
        kind: StationTimingOptionKindEnum.TrafficSlot,
        slotIndex,
      }),
      kind: StationTimingOptionKindEnum.TrafficSlot,
      seconds: slot.medianSeconds,
      datapoints: slot.datapoints,
      traffic: slot.traffic,
      hours: slot.hours,
    }),
  );

  const options = [
    plannedOption,
    medianOption,
    averageOption,
    ...trafficOptions,
  ].filter(option => option !== undefined);

  return {
    plannedOption,
    medianOption,
    averageOption,
    trafficOptions,
    options,
  };
};

/**
 * Threshold used when the response carries no `minDatapoints`, so an unannotated sample still has to
 * hold at least one measurement to be offered.
 */
const FALLBACK_MIN_DATAPOINTS = 1;

/**
 * One row per adjacent station pair of the edited entity. Backend pairs are consumed positionally as
 * the contract guarantees, but a row whose returned ids do not match its own stations is treated as
 * having no data rather than showing numbers that belong to another leg. `datapoints` carries the real
 * sample size even below the threshold, so a leg is only offered once it reaches `meta.minDatapoints`.
 */
export const buildStationTimingLegRows = ({
  stations,
  plannedLegSeconds,
  stats,
}: {
  stations: IStationTimingPreviewStation[];
  plannedLegSeconds: (number | undefined)[];
  stats: IStationTimingPairStatsDto | undefined;
}): IStationTimingLegRow[] => {
  const pairs = stats?.pairs || [];
  const minDatapoints = stats?.meta?.minDatapoints || FALLBACK_MIN_DATAPOINTS;

  return stations.slice(1).map((stationTo, legIndex) => {
    const stationFrom = stations[legIndex];
    const pairRaw = pairs[legIndex];
    const isPairAligned =
      pairRaw?.stationFromId === stationFrom.id &&
      pairRaw?.stationToId === stationTo.id;
    const pair = isPairAligned ? pairRaw : undefined;
    const hasData = Boolean(pair && pair.datapoints >= minDatapoints);
    const optionSet =
      hasData && pair
        ? buildLegOptionSet({
            legIndex,
            pair,
            plannedSeconds: plannedLegSeconds[legIndex],
          })
        : EMPTY_LEG_OPTION_SET;

    const legRow: IStationTimingLegRow = {
      legIndex,
      stationFromName: stationFrom.name,
      stationToName: stationTo.name,
      plannedSeconds: plannedLegSeconds[legIndex],
      datapoints: pair?.datapoints ?? 0,
      avgSeconds: pair?.avgSeconds ?? null,
      medianSeconds: pair?.medianSeconds ?? null,
      p10Seconds: pair?.p10Seconds ?? null,
      p90Seconds: pair?.p90Seconds ?? null,
      // Only a leg that is offered gets a breakdown: below the threshold there is nothing to break down.
      dayRows:
        hasData && pair
          ? buildStationTimingDayRows(pair.byDayOfWeek || [])
          : [],
      ...optionSet,
      hasData,
    };

    return legRow;
  });
};

/**
 * A long route is mostly legs the backend could not measure, and they bury the few actionable ones.
 * Splitting them is a display concern only: both groups keep the original leg order, every row lands in
 * exactly one group, and the selection helpers below still run over the undivided list.
 */
export const splitStationTimingLegRowsByData = (
  rows: IStationTimingLegRow[],
): IStationTimingLegRowGroups => ({
  rowsWithData: rows.filter(row => row.hasData),
  rowsWithoutData: rows.filter(row => !row.hasData),
});

/**
 * Medians are the default because measured leg times are heavily right-tail skewed (RYDE-7993):
 * a handful of stuck rides drags the average far above what a typical ride actually takes.
 */
export const buildStationTimingSelectionByKind = (
  rows: IStationTimingLegRow[],
  kind: StationTimingOptionKindEnum,
): TStationTimingSelectionMap => {
  const selection: TStationTimingSelectionMap = {};

  rows.forEach(row => {
    const option = row.options.find(legOption => legOption.kind === kind);

    if (option) {
      selection[row.legIndex] = option.key;
    }
  });

  return selection;
};

/**
 * The "nothing picked yet" selection every measured-times flow starts from and resets to before it
 * reopens the dialog, so a previous run's picks can never be applied to the next one.
 */
export const buildStationTimingEmptySelection =
  (): IStationTimingPreviewSelection => ({
    legSeconds: [],
    selectedLegIndexes: [],
  });

export const resolveStationTimingSelection = ({
  rows,
  selectionMap,
}: {
  rows: IStationTimingLegRow[];
  selectionMap: TStationTimingSelectionMap;
}): IStationTimingPreviewSelection => {
  const legSeconds: (number | undefined)[] = [];
  const selectedLegIndexes: number[] = [];

  rows.forEach(row => {
    const selectedKey = selectionMap[row.legIndex];
    const option = row.options.find(legOption => legOption.key === selectedKey);
    // Picking "planned" is how the user backs out of one leg, so it is not a measurement selection.
    const isMeasurementSelected = Boolean(
      option && option.kind !== StationTimingOptionKindEnum.Planned,
    );

    if (isMeasurementSelected) {
      selectedLegIndexes.push(row.legIndex);
    }

    // A leg with no measurement contributes nothing at all: the apply helpers keep its current delta,
    // which is the only value that survives an unsaved manual edit of a neighbouring station.
    const selectedSeconds =
      isMeasurementSelected && option?.seconds !== undefined
        ? Math.round(option.seconds)
        : undefined;
    legSeconds.push(selectedSeconds);
  });

  return { legSeconds, selectedLegIndexes };
};
